import { FC, useMemo, useState } from "react";
import { GetWithholdingDetailListServiceResponse, Withholding } from "../../../../../shared/interfaces/services/withholding-detail";
import { Table } from "antd";
import { ActionButton } from "@hdi-seguros/web-components.action-button";
import { DownloadOutlined } from "@ant-design/icons";
import { TableProps } from "antd/lib";
import CurrentAccountService from "../../../../../shared/services/currentAccount";
import { BINARY_VALUES, FILE_TYPES, Intermediary } from "../../../../../shared/interfaces";
import { displayErrorMessage, downloadBase64Files, formattedPercentage, renderCurrency } from "../../../../../shared/utils";
import dayjs from "dayjs";
import userHelper from "../../../../../shared/utils/userHelper";

interface WithholdingDetailTableProps {
    withholdingList: GetWithholdingDetailListServiceResponse;
    isLoading: boolean;
    selectedProducer: Intermediary | undefined;
    onSelectionChange: (selectedRows: Withholding[]) => void;
}

export const WithholdingTable: FC<WithholdingDetailTableProps> = ({ withholdingList, isLoading, onSelectionChange, selectedProducer }) => {
    const [currentPage, setCurrentPage] = useState<number>(1);
    const [selectionType, setSelectionType] = useState<'checkbox'>('checkbox');
    const [downloadingStates, setDownloadingStates] = useState<Record<string, boolean>>({});
    const [pageSize, setPageSize] = useState<number>(10);

    const userInfo = userHelper.getUser();
    
    const currentAccount = `${userInfo?.intermediarioPrincipal!.codMayorAux}-${userInfo?.intermediarioPrincipal!.codigoInter!}`;

    const HEADERS_BALANCE_TABLE = useMemo(() => [
        {
            title: 'Impuesto',
            dataIndex: 'tipoImpuesto',
            key: 'tipoImpuesto',
        },  
        {
            title: 'Provincia',
            dataIndex: 'provincia',
            key: 'provincia',
        },
        {
            title: 'Mes - Año',
            dataIndex: 'anioymes',
            key: 'anioymes',
        },
        {
            title: 'Base Imponible',
            dataIndex: 'baseImponible',
            render: (value: string) => `${renderCurrency(value)}`,
            key: 'baseImponible',
        },
        {
            title: 'Cuenta Corriente',
            render: () => `${currentAccount}`,
        },
        {
            title: '% Retención',
            dataIndex: 'porcRetencion',
            render: (value: string) => `${formattedPercentage(value)}`,
            key: 'porcRetencion',
        },
        {
            title: 'Importe Retención',
            dataIndex: 'importeRetencion',
            render: (value: string) => `${renderCurrency(value)}`,
            key: 'importeRetencion',
        },
        {
            title: 'Nro. Certificado',
            dataIndex: 'nroCertificado',
            key: 'nroCertificado',
        },
        {
            title: 'Fecha Retención',
            dataIndex: 'fechaRetencion',
            render: (value: string) => `${dayjs(value).format('DD/MM/YYYY')}`,
            key: 'fechaRetencion',
        },
        {
            title: 'Acciones',
            key: 'acciones',
            width: '10%',
            className: 'withholding-list__download-collumn',
            render: (record: Withholding) => (
                <ActionButton
                    icon={<DownloadOutlined />}
                    label="Descargar"
                    className='btn-accent-outline-barbuss'
                    type="secondary"
                    loading={downloadingStates[record.key.toString()] || false}
                    onClick={() => downloadFile(record)}
                    disabled={record.nroCertificado === BINARY_VALUES.ZERO}
                />
            ),
        },
        ], [downloadingStates]
    );

    const downloadFile = async (fileToDownload: Withholding) => {        
        setDownloadingStates(prevStates => ({
            ...prevStates,
            [fileToDownload.key.toString()]: true,
        }));

        try {
    
            const generatedFiles = await CurrentAccountService.postDownloadWiholdingDetailsFiles([fileToDownload], selectedProducer!);
           
            generatedFiles.forEach(file => {
                downloadBase64Files(file.base64, file.fileName, FILE_TYPES.PDF)
            });
        
        } catch (error) {} finally {
            setDownloadingStates(prevStates => ({
                ...prevStates,
                [fileToDownload.key.toString()]: false,
            }));
        }
    };

    const rowSelection: TableProps<any>['rowSelection'] = {
        onChange: (selectedRowKeys: React.Key[], selectedRows: any[]) => {
            onSelectionChange(selectedRows);
        },
        getCheckboxProps: (record: any) => ({
            disabled: record.nroCertificado === BINARY_VALUES.ZERO,
        }),
    };

    const dataSource = useMemo(
        () =>
          withholdingList.retenciones?.map((item, index) => ({
            ...item,
            key: item.key || `key-${index}`,
          })) || [],
        [withholdingList]
    );

    return (
        <Table
            rowSelection={{ type: selectionType, ...rowSelection }}
            loading={isLoading}
            bordered
            className='w-100'
            columns={HEADERS_BALANCE_TABLE}
            dataSource={dataSource}
            pagination={{
                current: currentPage,
                pageSizeOptions: ['10', '20', '50', '100'],
                pageSize: pageSize,
                total: withholdingList.retenciones?.length,
                showSizeChanger: true,
                showTotal: (total) =>
                    total === 1 ? 'Una retención' : `${total} retenciones`,
                onChange: (page, pageSize) => {
                    setCurrentPage(page);
                    setPageSize(pageSize);
                  },
            }}
        />
    );
};